/* eslint-disable max-statements, no-console, no-undef */
import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { LoginContext } from "../components/au.configuration/component";
import { openLoginPopup, openSecondaryIdentifierModal, closeSecondaryIdentifierModal } from "../components/au.configuration/actions";
const pushCleverTapEvent = (eventName, data = {}) => {
    try { if (typeof window !== "undefined" && window.clevertap) window.clevertap.event.push(eventName, data); } catch (e) { /* ignore */ }
};

// useLoginGate — centralizes the "login → check profile → proceed" pattern.
//
// config.requiredFields       - Profile fields to require: "profileName", "email", "mobile"
// config.incompleteProfileAction - "basicDetails" (opens modal directly) or "secondaryIdentifier" (session flag)
// config.closableModal        - Whether the modal is closable (default: false)
// config.autoTrigger          - Whether to trigger the gate on mount (default: false)
// config.onComplete           - Called when all gates are satisfied
//
// Returns { trigger, isPending }

const useLoginGate = ({
    requiredFields = ["profileName", "email"],
    incompleteProfileAction = "basicDetails",
    closableModal = false,
    autoTrigger = false,
    persistKey = "",
    onComplete
} = {}) => {
    const dispatch = useDispatch();
    const { setOnLoginSuccessCallback, setOnLoginCloseCallback } = useContext(LoginContext);

    // Check sessionStorage for persisted pending state (survives page reload after SSO)
    const getPersistedPending = () => {
        if (!persistKey) return false;
        try { return window.sessionStorage.getItem(persistKey) === "1"; } catch (e) { return false; }
    };
    const [isPending, setIsPending] = useState(getPersistedPending);

    // Refs for latest values — avoids stale closures in effects
    const onCompleteRef = useRef(onComplete);
    onCompleteRef.current = onComplete;

    const configRef = useRef({ requiredFields, incompleteProfileAction, closableModal, persistKey });
    configRef.current = { requiredFields, incompleteProfileAction, closableModal, persistKey };

    // Redux state
    const isLoggedIn = useSelector(state => !!state.user?.isLoggedIn);
    const isUmsProfileFetched = useSelector(state => !!state.user?.isUmsProfileFetched);
    const profileName = useSelector(state => state.user?.profileName || "");
    const email = useSelector(state => state.user?.email || "");
    const mobile = useSelector(state => state.user?.mobile || "");

    const clearPersist = () => {
        const { persistKey: pk } = configRef.current;
        if (pk) {
            try { window.sessionStorage.removeItem(pk); } catch (e) { /* ignore */ }
        }
    };

    // Stable trigger via ref indirection
    const triggerRef = useRef();
    // eslint-disable-next-line complexity
    triggerRef.current = () => {
        const { requiredFields: rf, incompleteProfileAction: ipa, closableModal: cm, persistKey: pk } = configRef.current;
        const fields = { profileName, email, mobile };
        const isComplete = rf.every(f => !!fields[f]);

        console.log("[DEBUG] loginGate.trigger()", { isLoggedIn, isUmsProfileFetched, isComplete, fields, rf, ipa });

        // All gates already satisfied — complete immediately
        if (isLoggedIn && isUmsProfileFetched && isComplete) {
            clearPersist();
            // eslint-disable-next-line no-console
            console.log("[DEBUG] loginGate: gate passed immediately (already complete)");
            pushCleverTapEvent("Login Gate Passed", { trigger: "already_logged_in" });
            if (onCompleteRef.current) onCompleteRef.current();
            return;
        }

        setIsPending(true);
        if (pk) {
            try { window.sessionStorage.setItem(pk, "1"); } catch (e) { /* ignore */ }
        }

        if (!isLoggedIn) {
            // Not logged in — open login popup
            // eslint-disable-next-line no-console
            console.log("[DEBUG] loginGate: not logged in, opening login popup");
            pushCleverTapEvent("Login Modal Opened", { requiredFields: rf.join(",") });
            if (ipa === "secondaryIdentifier") {
                try { window.sessionStorage.setItem("bifrost_sso_pending_secondary", "1"); } catch (e) { /* ignore */ }
            }
            setOnLoginSuccessCallback(() => {});
            setOnLoginCloseCallback(() => {
                pushCleverTapEvent("Login Modal Closed", { reason: "user_dismissed" });
                setIsPending(false);
                clearPersist();
                if (ipa === "secondaryIdentifier") {
                    try { window.sessionStorage.removeItem("bifrost_sso_pending_secondary"); } catch (e) { /* ignore */ }
                }
            });
            dispatch(openLoginPopup());
        } else if (isUmsProfileFetched) {
            // Logged in, profile fetched, but incomplete — open appropriate modal
            // eslint-disable-next-line no-console
            console.log("[DEBUG] loginGate: logged in, profile fetched, incomplete -> opening modal");
            dispatch(openSecondaryIdentifierModal({ closable: cm, skippable: false }));
        } else {
            // eslint-disable-next-line no-console
            console.log("[DEBUG] loginGate: logged in but profile NOT yet fetched, waiting for reactive effect");
        }
    };

    const trigger = useCallback(() => triggerRef.current(), []);

    // Auto-trigger on mount (or resume from persisted pending state after page reload)
    useEffect(() => {
        if (autoTrigger || isPending) trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoTrigger, trigger]);

    // Reactive effect: watches state transitions after trigger() and completes the flow
    useEffect(() => {
        if (!isPending || !isLoggedIn || !isUmsProfileFetched) return;

        // loginGate owns the post-login flow — clear SSO flag so au.configuration
        // doesn't also open SecondaryIdentifierModal (which would conflict)
        try { window.sessionStorage.removeItem("bifrost_sso_pending_secondary"); } catch (e) { /* ignore */ }

        const { requiredFields: rf, incompleteProfileAction: ipa, closableModal: cm } = configRef.current;
        const fields = { profileName, email, mobile };
        const isComplete = rf.every(f => !!fields[f]);

        console.log("[DEBUG] loginGate reactive effect", { isPending, isLoggedIn, isUmsProfileFetched, isComplete, fields, rf, ipa });

        if (!isComplete) {
            // Profile still incomplete — ensure modal is open
            // eslint-disable-next-line no-console
            console.log("[DEBUG] loginGate: profile incomplete, opening modal");
            dispatch(openSecondaryIdentifierModal({ closable: cm, skippable: false }));
            return;
        }

        // All gates satisfied
        console.log("[DEBUG] loginGate: all gates satisfied, calling onComplete");
        dispatch(closeSecondaryIdentifierModal());
        setIsPending(false);
        clearPersist();
        pushCleverTapEvent("Login Gate Passed", { trigger: "after_login" });
        if (onCompleteRef.current) onCompleteRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPending, isLoggedIn, isUmsProfileFetched, profileName, email, mobile]);

    return { trigger, isPending };
};

export default useLoginGate;
